/**
 * V70-S — 연장 근무 관리 표시·엑셀 규칙 (순수 함수)
 *
 * 내부 단위 = 분(정수). 화면은 "시간:분"(H:MM)으로 표시해 분 단위 정밀도를 잃지 않는다.
 * 엑셀은 같은 H:MM 문자열 + 정확한 분(숫자) 컬럼을 함께 둔다.
 */
import { toInclusiveWorkDisplay } from '../../shared/utils/approvedWorkDisplay';

export function formatMinutesHm(minutes: number | null | undefined): string {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes)) return '-';
  const sign = minutes < 0 ? '-' : '';
  const abs = Math.abs(Math.round(minutes));
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
}

/** 대체휴무 일수 표시 (0.125 단위까지 그대로) */
export function formatDays(days: number | null | undefined): string {
  if (typeof days !== 'number' || !Number.isFinite(days)) return '-';
  return String(Math.round(days * 1000) / 1000);
}

export const ORIGINAL_TYPE_LABELS: Record<string, string> = {
  overtime: '연장근무 신청',
  weekend: '휴일근무 신청',
  holiday: '휴일근무 신청(구)',
};

export const EXCEPTION_LABELS: Record<string, string> = {
  invalid_time: '시각 오류',
  minutes_mismatch: '승인시간 불일치',
  overlap: '시간 중복',
  company_holiday_unconfirmed: '회사 휴무일(확인 필요)',
  other_off_day_unconfirmed: '금요일 외 휴무일(확인 필요)',
};

export interface OvertimeEmployeeRow {
  employeeId: string;
  name: string;
  orgUnitName: string;
  positionName: string;
  jobTitleName: string;
  status: string;
  outOfScope: boolean;
  /** 서버 원본(V70-S-2 상호 배타) — 일반 근무일 06~22. 표시는 toInclusiveWorkDisplay 로 변환 (V70-S-5) */
  overtimeMinutes: number;
  /** 일반 근무일 22~06 */
  nightMinutes: number;
  /** 휴일 06~22 */
  holidayMinutes: number;
  /** 휴일 22~06 */
  holidayNightMinutes: number;
  /** 연장 + 야간 + 휴일 + 휴일 야간 */
  totalMinutes: number;
  compOffGrantMinutes: number;
  requestCount: number;
  exceptionCount: number;
}

export interface OvertimeRequestRow {
  id: string;
  employeeId: string;
  date: string;
  startTime: string;
  endTime: string;
  endDateOffset: number;
  originalType: string;
  status: string;
  approvedMinutes: number | null;
  spanMinutes: number | null;
  modified: boolean;
  overtimeMinutes: number;
  holidayMinutes: number;
  nightMinutes: number;
  holidayNightMinutes: number;
  inMonthSpanMinutes: number;
  exception: { code: string; message: string } | null;
  segments: Array<{ date: string; start: string; end: string; minutes: number; nightMinutes: number; inMonth: boolean; kind: string | null; dayLabel: string | null }>;
}

export interface CompOffGrantDetailRow {
  id: string;
  employeeId: string;
  grantedDate: string;
  grantedAt: string;
  days: number;
  minutes: number;
  inputType: string;
  inputValue: number | null;
  reason: string;
  status: string;
  cancelled: boolean;
  counted: boolean;
}

export function approvedRangeText(r: Pick<OvertimeRequestRow, 'startTime' | 'endTime' | 'endDateOffset'>): string {
  return `${r.startTime} ~ ${r.endDateOffset === 1 ? '익일 ' : ''}${r.endTime}`;
}

/** 재분류 구간 설명 (예: "10-10 토요일 23:00~24:00 / 10-11 일요일 00:00~02:00") */
export function segmentText(r: OvertimeRequestRow): string {
  return r.segments
    .map((g) => `${g.date.slice(5)} ${g.inMonth ? (g.dayLabel || '') : '(다른 달)'} ${g.start}~${g.end}`.replace(/\s+/g, ' ').trim())
    .join(' / ');
}

// ── 엑셀 행 ─────────────────────────────────────────────────

// V70-S-5: 4개 근무시간은 표시 기준(연장⊇야간, 휴일⊇휴일 야간)으로 출력. 화면과 같은 toInclusiveWorkDisplay 사용
export function deriveOvertimeExportRow(e: OvertimeEmployeeRow): Record<string, string | number> {
  const d = toInclusiveWorkDisplay(e);
  return {
    '이름': e.name || '-',
    '소속 부서': e.orgUnitName || '-',
    '직위': e.positionName || '-',
    '직책': e.jobTitleName || '-',
    '연장근무(시간:분)': formatMinutesHm(d.overtime),
    '야간근무(시간:분)': formatMinutesHm(d.night),
    '휴일근무(시간:분)': formatMinutesHm(d.holiday),
    '휴일 야간근무(시간:분)': formatMinutesHm(d.holidayNight),
    '대체휴무 지급(시간:분)': formatMinutesHm(e.compOffGrantMinutes),
    '연장근무(분)': d.overtime,
    '야간근무(분)': d.night,
    '휴일근무(분)': d.holiday,
    '휴일 야간근무(분)': d.holidayNight,
    '승인 근무 합계(분)': e.totalMinutes,
    '대체휴무 지급(분)': e.compOffGrantMinutes,
    '집계 예외(건)': e.exceptionCount,
    '비고': e.outOfScope ? '대상 기준 외 — 해당 월 기록 있음' : '',
  };
}

export function deriveRequestExportRow(r: OvertimeRequestRow, nameOf: (id: string) => string): Record<string, string | number> {
  const d = toInclusiveWorkDisplay(r);
  return {
    '이름': nameOf(r.employeeId),
    '근무일자': r.date,
    // V70-S-4: '승인 신청 ID' 는 엑셀에도 노출하지 않는다 (r.id 는 내부 식별용으로 유지)
    '원래 신청 유형': ORIGINAL_TYPE_LABELS[r.originalType] || r.originalType || '-',
    '승인 시각': approvedRangeText(r),
    '최종 승인시간(분)': r.approvedMinutes ?? '-',
    '해당 월 귀속(분)': r.inMonthSpanMinutes,
    '연장근무(분)': d.overtime,
    '야간근무(분)': d.night,
    '휴일근무(분)': d.holiday,
    '휴일 야간근무(분)': d.holidayNight,
    '재분류 구간': segmentText(r),
    '승인 상태': r.status === 'approved' ? '승인' : r.status,
    '승인 후 수정': r.modified ? '예' : '아니오',
    '집계 예외': r.exception ? `${EXCEPTION_LABELS[r.exception.code] || r.exception.code}: ${r.exception.message}` : '',
  };
}

export function deriveGrantExportRow(g: CompOffGrantDetailRow, nameOf: (id: string) => string): Record<string, string | number> {
  return {
    '이름': nameOf(g.employeeId),
    '지급일': g.grantedDate,
    '지급량(일)': g.days,
    '환산시간(시간:분)': formatMinutesHm(g.minutes),
    '환산시간(분)': g.minutes,
    '입력 방식': g.inputType === 'hours' ? `시간 입력 (${g.inputValue ?? '-'}시간)` : g.inputType === 'days' ? '일수 입력' : g.inputType || '-',
    '지급 사유': g.reason || '-',
    '상태': g.cancelled ? '취소' : g.status === 'active' ? '유효' : g.status || '-',
    '합계 반영': g.counted ? '예' : '아니오',
  };
}
